import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent,
  DialogTitle, IconButton, InputAdornment, List, ListItemButton,
  ListItemText, TextField, Typography,
} from '@mui/material'
import CloseIcon  from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { loadLockdowns, loadLockdownDetail } from '../../reducers/lockdownReducer'

const TECH_COLORS = {
  '5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
  '4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  '3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
  '2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
}

export default function LockdownKPIDashboardLockdownPicker({ open, onClose, onConfirm }) {
  const dispatch = useDispatch()
  const { list, listLoading } = useSelector(s => s.lockdown)

  const [search,  setSearch]  = useState('')
  const [pending, setPending] = useState(null)

  // Load list on open
  useEffect(() => {
    if (open) dispatch(loadLockdowns({ search: search || undefined }))
  }, [open]) // eslint-disable-line

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(loadLockdowns({ search: search || undefined }))
    }, 350)
    return () => clearTimeout(t)
  }, [search, dispatch])

  const handleConfirm = async () => {
    if (!pending) return
    const result = await dispatch(loadLockdownDetail(pending.lockdownId))
    const detail = result.payload
    onConfirm(detail)
    onClose()
  }

  const techChip = (tech) => {
    const c = TECH_COLORS[tech] ?? TECH_COLORS['4G']
    return (
      <Chip key={tech} label={tech} size="small"
        sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 9, height: 18 }} />
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={600} fontSize={15}>Select Lockdown</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      {/* Search */}
      <Box sx={{ px: 3, pb: 1 }}>
        <TextField
          fullWidth size="small" placeholder="Search by name or technology…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
          autoFocus
        />
      </Box>

      <Box sx={{ px: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
          {list.length} lockdowns
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
        {listLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
          : list.length === 0
            ? <Typography align="center" color="text.disabled" sx={{ p: 4 }}>No lockdowns found</Typography>
            : (
              <List dense disablePadding>
                {list.map(l => (
                  <ListItemButton
                    key={l.lockdownId}
                    selected={pending?.lockdownId === l.lockdownId}
                    onClick={() => setPending(l)}
                    sx={{
                      borderBottom: '1px solid', borderColor: 'divider', px: 2.5, py: 1.25,
                      '&.Mui-selected':       { bgcolor: 'primary.light' },
                      '&.Mui-selected:hover': { bgcolor: '#bbdefb' },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography fontSize={13} fontWeight={500}>{l.lockdownName}</Typography>
                      }
                      secondary={
                        <Typography fontSize={11} color="text.disabled" sx={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {l.siteCount != null ? `${l.siteCount} sites` : ''}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1.5, flexShrink: 0 }}>
                      {l.technology
                        ? l.technology.split(',').map(t => techChip(t.trim()))
                        : null}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )
        }
      </DialogContent>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" size="small" onClick={onClose}>Cancel</Button>
        <Button variant="contained" size="small" disabled={!pending} onClick={handleConfirm}>
          Confirm selection
        </Button>
      </Box>
    </Dialog>
  )
}
